import { BottomSheet } from "@nykaa/payment-ui/src/BottomSheet/BottomSheet";
import { getPlatform } from "../../helpers";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import React, { useContext } from "react";
import { IPaymentWrapperConfig } from "./@types";
import { PaymentFailure } from "./Loadable";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { get } from "logics/src/utils/helpers";
import { getStringFromId } from "helpers/vernacular";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { getPaymentFailureDataUsingPaymentMode } from "layouts/Payment/common/dependencies/dependencyhelpers";
import { allTrackingHandlerForApps } from "../../helpers/utils/trackingHandler";
import { TRACKING_EVENTS } from "../../constants";
import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { trackPaymentFailureBottomSheetActions } from "layouts/Payment/common/dependencies/tracking";
import { MASTER_PAYMENT_STATUS } from "constants/paymentStatus";
import { getOrdersPaymentResponse as getPaymentResponseApi, offerReversal } from "store/payment/services";
import { CLIENT_URLS } from "constants/urls";
import { doRedirect } from "helpers/utils/redirection";
import { useHistory } from "react-router-dom";
import { ExperimentAndSwitchContext } from "wrapper/ExperimentAndSwitchWrapper/context";

/**
 * @description Wrapper Component to
 * render PaymentFailure Bottomsheet along with callback functions
 * findout fallbackPaymentOptions, fallbackMessages and nonFallbackMessages
 */
export const PaymentFailureWrapper = (props: IPaymentWrapperConfig) => {
  const {
    theme,
    setShowFallbackModeSheet,
    isGPayIntentAvailable,
    timerCallback,
    showBottomSheet,
  } = props;
  const paymentContext = useContext(PaymentContext);
  const { applicationData: { isApp, deviceType } } = useContext(ApplicationContext);
  const { OfferReversal } = useContext(ExperimentAndSwitchContext);
  const paymentInfo = get(paymentContext, "paymentInfo");
  const setPaymentResponseV2Data = get(paymentContext, "setPaymentResponseV2Data");
  const paymentResponseV2Data = get(paymentContext, "paymentResponseV2Data");
  const setLoading = get(paymentContext, "setLoading");
  const fallbackModes = paymentInfo?.fallbackModes;
  const masterPaymentStatus = get(paymentResponseV2Data, "masterPaymentStatus");
  const isSavedPayment = get(paymentResponseV2Data, "isSavedPayment");
  const orderId = get(paymentResponseV2Data, "orderId");
  const previousPaymentMode = get(paymentResponseV2Data, "paymentMode");
  const history = useHistory();
  const {
    fallbackMessages,
    nonFallbackMessages,
    fallbackPaymentOptions,
  } = getPaymentFailureDataUsingPaymentMode(
    fallbackModes,
    masterPaymentStatus,
    previousPaymentMode,
  );


  const retryButtonText = getStringFromId(
    VERNACULAR_STRING_IDS.PAYMENT_FAILED_RETRY_BUTTON,
    VERNACULAR_DEFAULT_MESSAGES.PAYMENT_FAILED_RETRY_BUTTON
  );
  const platform = getPlatform();
  const bottomSheetCloseIcon = getIcons(platform, ICONS_NAMES.CLEAR);

  const clearPaymentResponseV2Data = () => {
    setPaymentResponseV2Data({
      ...paymentResponseV2Data,
      paymentStatus: "",
      masterPaymentStatus: "",
      showFallbackModes: false,
      paymentMode: "",
      orderId: "",
    });
  };


 /**
  * In case of PENDING masterPaymentStatus, hit the paymentSuccess Api and
  * redirect user to success page if masterPaymentStatus is success
  */
  const handleSuccessRedirection = async () => {
    if (masterPaymentStatus === MASTER_PAYMENT_STATUS.PENDING) {
      // hit payment success Api
      try {
        setLoading(true); // Remove later if required -- setShowLottie
        let paymentResponseData = await getPaymentResponseApi({ orderId });
        setLoading(false); // Remove later if required -- setShowLottie
        if (paymentResponseData?.masterPaymentStatus === MASTER_PAYMENT_STATUS.SUCCESS) {
          const hashValue = paymentResponseData?.hashValue;
          // Redirecting to postpayment for further redirections
          let redirectionUrl = `${CLIENT_URLS.POST_PAYMENT_PAGE}?orderId=${orderId}&isOrderSuccess=true`;
          if (hashValue) {
            redirectionUrl = `${redirectionUrl}&hashvalue=${hashValue}`;
          }
          doRedirect({
            url: redirectionUrl,
            history,
            isClientRedirection: true,
          });
          return false;
        }
        return true;
      } catch (e) {
        console.log("Error in handleSuccessRedirection");
        setLoading(false); // Remove later if required -- setShowLottie
      }
    } else {
      return true;
    }
  };

  /**
   * @description -- function to close the bottomsheet on cross icon click or on retry button click
   */
  const handleBottomSheetClose = async (eventName: string) => {
    const query = new URLSearchParams(history.location.search);
    // query is used if in case user refreshes the page
    const failureOrderId = orderId || query.get("paymentFailureOrderId");

    let offerReversalParam = {
      orderId: failureOrderId || "",
      mode: previousPaymentMode,
      deviceType,
    };

    let offerReversalApiCall: any = () => Promise.resolve();
    if (OfferReversal.experimentEnabled) {
      offerReversalApiCall = offerReversal;
    }


    /**
     * calling offer Reversal api before redirection
     */
    setLoading(true);
    offerReversalApiCall(offerReversalParam).finally(async () => {
      setLoading(false);
      let noSuccessRedirection = await handleSuccessRedirection();
      if (noSuccessRedirection) {
        // handling for redirectionUrl on the basis of isSavedPayment boolean value
        const redirectionUrl = isSavedPayment
          ? `${CLIENT_URLS.PAYMENT_BASE_URL}?paymentFailureOrderId=${failureOrderId}`
          : `${CLIENT_URLS.PAYMENT_BASE_URL}?paymentFailureOrderId=${failureOrderId}&screen=newPayment`;
        clearPaymentResponseV2Data();
        history.replace(redirectionUrl);

        allTrackingHandlerForApps({ event: eventName, isApp });
        //tracking for web
        trackPaymentFailureBottomSheetActions({ event: eventName });
      }
    });
  };

  return (
    <BottomSheet
      isVisible={showBottomSheet}
      leadingIcon={bottomSheetCloseIcon}
      onIconClick={() => handleBottomSheetClose(TRACKING_EVENTS.FAILURE_BOTTOMSHEET_CROSS_BUTTON_CLICK)}
      theme={theme}
      maxHeight={100}
      zIndex={2}
    >
      <PaymentFailure
          fallbackPaymentOptions={fallbackPaymentOptions}
          fallbackMessages={fallbackMessages}
          isGPayIntentAvailable={isGPayIntentAvailable}
          timerCallback={timerCallback}
          setShowFallbackModeSheet={setShowFallbackModeSheet}
          nonFallbackMessages={nonFallbackMessages}
          onRetryButtonClick={() => handleBottomSheetClose(TRACKING_EVENTS.FAILURE_BOTTOMSHEET_RETRY_BUTTON_CLICK)}
          retryButtonText={retryButtonText}
          handleSuccessRedirection = {handleSuccessRedirection}
        />
    </BottomSheet>
  );
};
